import type { PortfolioCompanySummary, PortfolioRound, PortfolioSource } from '@nemo/api';
import { formatRoundDate, timeSinceRaiseText } from '@nemo/core/model';
import { formatRoundLabel } from '../dealroom/format.js';
import { formatEur } from '../ui/format.js';

export const SOURCE_LABELS: Record<PortfolioSource, string> = {
  quantium: 'Quantium',
  overview: 'overview',
  closedDeals: 'closed deals',
  affinity: 'Affinity',
};

/** "Quantium · overview · Affinity": where the record came from. */
export function sourcesText(company: Pick<PortfolioCompanySummary, 'sources'>): string {
  return company.sources.map((source) => SOURCE_LABELS[source]).join(' · ');
}

/** The organisation's page in Affinity, where the deployment says where Affinity is. */
export function affinityOrganizationUrl(
  webBase: string | null,
  organizationId: string | null,
): string | null {
  if (webBase === null || organizationId === null) return null;
  return `${webBase.replace(/\/+$/, '')}/companies/${encodeURIComponent(organizationId)}`;
}

/** A person's page in the deployment's Affinity workspace; null where either is unknown. */
export function affinityPersonUrl(webBase: string | null, personId: string | null): string | null {
  if (webBase === null || personId === null) return null;
  return `${webBase.replace(/\/+$/, '')}/persons/${encodeURIComponent(personId)}`;
}

/** How many co-investor names a row shows before it counts the rest. */
const CO_INVESTORS_SHOWN = 3;

/** "Series B · Mar 2026", the way a round is said. */
export function roundTitle(
  round: Pick<PortfolioRound, 'roundLabel' | 'announcedOn' | 'source' | 'datePrecision'>,
): string {
  return [formatRoundLabel(round.roundLabel), formatRoundDate(round)]
    .filter((part) => part !== '')
    .join(' · ');
}

/** "3 · Series B · Mar 2026": how many, then the newest. */
export function roundsText(company: PortfolioCompanySummary): string {
  if (company.roundCount === 0 || company.latestRound === null) return '';
  return `${company.roundCount} · ${roundTitle(company.latestRound)}`;
}

/** "Accel, Index, Kinnevik +2". */
export function coInvestorsText(company: PortfolioCompanySummary): string {
  const names = company.coInvestors.map((row) => row.fundName);
  const shown = names.slice(0, CO_INVESTORS_SHOWN);
  const rest = names.length - shown.length;
  return `${shown.join(', ')}${rest > 0 ? ` +${rest}` : ''}`;
}

/** "€25M", or "$3M" as announced where the round was not in euros. */
export function roundAmount(round: PortfolioRound): string {
  if (round.amountEur !== null) return formatEur(round.amountEur);
  if (round.amount === null) return '';
  return `${round.currency ?? ''} ${formatCompact(round.amount)}`.trim();
}

function formatCompact(amount: number): string {
  if (amount >= 1_000_000) return `${Number((amount / 1_000_000).toFixed(1))}M`;
  if (amount >= 1_000) return `${Number((amount / 1_000).toFixed(1))}K`;
  return String(amount);
}

/** "Series A · Mar 2025 · 7 mo ago"; null where nothing says when it last raised. */
export function lastRaiseText(company: Pick<PortfolioCompanySummary, 'lastRaise'>): string | null {
  const raise = company.lastRaise;
  if (raise === null) return null;
  const date = formatRoundDate({
    announcedOn: raise.raisedOn,
    // Every raise is dated to a month or a year; a Dealroom-sourced date reads as its month.
    source: 'dealroom',
    datePrecision: raise.datePrecision,
  });
  const ago = timeSinceRaiseText(raise);
  return [formatRoundLabel(raise.roundLabel), date, ago === date ? '' : ago]
    .filter((part) => part !== '')
    .join(' · ');
}

/** "98", or Dealroom's band ("51-200") where it has no count. */
export function employeesText(
  company: Pick<PortfolioCompanySummary, 'employees' | 'employeesRange'>,
): string | null {
  if (company.employees !== null) return String(company.employees);
  return company.employeesRange;
}
